<script setup>
// Modelos de e-mail: lista + galeria + construtor (rota própria). Modelos
// antigos (GrapesJS) continuam abrindo no editor avançado, aqui mesmo.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import GrapesEmailEditor from './GrapesEmailEditor.vue';
import GalleryModal from './GalleryModal.vue';
import ImportHtmlDialog from './ImportHtmlDialog.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const templates = ref([]);
const loading = ref(false);
const saving = ref(false);
const editing = ref(null); // editor avançado (legado): { id, name, subject, design }
const grapesRef = ref(null);
const galleryRef = ref(null);
const importRef = ref(null);
const confirmRef = ref(null);

const VARIABLES = [
  '{{nome}}',
  '{{empresa}}',
  '{{email}}',
  '{{remetente}}',
  '{{endereco}}',
  '{{unsubscribe_url}}',
];

const tp = key => t(`EMAIL_TEMPLATES.${key}`);

async function fetchTemplates() {
  loading.value = true;
  try {
    const { data } = await API.get();
    templates.value = data;
  } catch {
    useAlert(tp('LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

const openBuilder = tpl => {
  router.push({
    name: 'email_templates_builder',
    params: { accountId: route.params.accountId, templateId: tpl.id },
  });
};

function editTemplate(tpl) {
  if (tpl.builder) {
    openBuilder(tpl);
    return;
  }
  editing.value = {
    id: tpl.id,
    name: tpl.name,
    subject: tpl.subject || '',
    design: tpl.design || {},
  };
}

async function duplicateTemplate(tpl) {
  try {
    const { data } = await API.duplicate(tpl.id);
    await fetchTemplates();
    if (data.builder) openBuilder(data);
  } catch {
    useAlert(tp('SAVE_ERROR'));
  }
}

async function deleteTemplate(tpl) {
  const ok = await confirmRef.value?.ask({
    title: tp('DELETE'),
    description: tp('DELETE_CONFIRM'),
    confirmLabel: tp('DELETE'),
  });
  if (!ok) return;
  try {
    await API.delete(tpl.id);
    await fetchTemplates();
  } catch {
    useAlert(tp('SAVE_ERROR'));
  }
}

function cancelEdit() {
  editing.value = null;
}

async function saveTemplate() {
  const tpl = editing.value;
  if (!tpl.name.trim()) {
    useAlert(tp('NAME_REQUIRED'));
    return;
  }
  const { html, design } = grapesRef.value.getContent();
  saving.value = true;
  try {
    await API.update(tpl.id, {
      marketing_email_template: {
        name: tpl.name.trim(),
        subject: tpl.subject,
        body_html: html,
        design,
      },
    });
    useAlert(tp('SAVED'));
    editing.value = null;
    await fetchTemplates();
  } catch (error) {
    useAlert(error?.response?.data?.message || tp('SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
}

const modeLabel = tpl =>
  tpl.builder
    ? t(`EMAIL_TEMPLATES.BUILDER.MODE.${tpl.mode}`)
    : tp('LEGACY_BADGE');
const formatDate = value =>
  value ? new Date(value).toLocaleDateString() : '—';

onMounted(fetchTemplates);
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background">
    <template v-if="!editing">
      <div class="flex h-full w-full flex-col overflow-auto p-6">
        <div class="mb-4 flex items-center justify-between">
          <div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ tp('HEADER') }}
            </h1>
            <p class="mt-1 text-sm text-n-slate-11">{{ tp('DESCRIPTION') }}</p>
          </div>
          <button
            class="flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand-dark"
            @click="galleryRef?.open()"
          >
            <span class="i-lucide-plus" />
            {{ tp('NEW') }}
          </button>
        </div>

        <div v-if="loading" class="py-10 text-center text-sm text-n-slate-11">
          {{ tp('LOADING') }}
        </div>
        <div
          v-else-if="!templates.length"
          class="rounded-lg bg-n-solid-2 py-10 text-center text-sm text-n-slate-11"
        >
          {{ tp('EMPTY') }}
        </div>
        <table v-else class="w-full divide-y divide-n-weak text-sm">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-4 font-medium">{{ tp('COL_NAME') }}</th>
              <th class="py-2 pr-4 font-medium">{{ tp('COL_MODE') }}</th>
              <th class="py-2 pr-4 font-medium">{{ tp('COL_SUBJECT') }}</th>
              <th class="py-2 pr-4 font-medium">{{ tp('COL_UPDATED') }}</th>
              <th class="py-2" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-for="tpl in templates" :key="tpl.id">
              <td class="py-3 pr-4 font-medium">
                <button
                  type="button"
                  class="hover:text-n-brand"
                  @click="editTemplate(tpl)"
                >
                  {{ tpl.name }}
                </button>
              </td>
              <td class="py-3 pr-4">
                <span
                  class="rounded-md px-1.5 py-0.5 text-[11px] font-medium"
                  :class="
                    tpl.builder
                      ? tpl.mode === 'personal'
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-brand/10 text-n-brand'
                      : 'bg-n-solid-3 text-n-slate-11'
                  "
                >
                  {{ modeLabel(tpl) }}
                </span>
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ tpl.subject || '—' }}
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ formatDate(tpl.updated_at) }}
              </td>
              <td class="whitespace-nowrap py-3 text-right">
                <button
                  class="px-2 text-n-slate-11 hover:text-n-brand"
                  :title="tp('EDIT')"
                  @click="editTemplate(tpl)"
                >
                  <span class="i-lucide-pencil" />
                </button>
                <button
                  class="px-2 text-n-slate-11 hover:text-n-brand"
                  :title="tp('DUPLICATE')"
                  @click="duplicateTemplate(tpl)"
                >
                  <span class="i-lucide-copy" />
                </button>
                <button
                  class="px-2 text-n-slate-11 hover:text-n-ruby-9"
                  :title="tp('DELETE')"
                  @click="deleteTemplate(tpl)"
                >
                  <span class="i-lucide-trash-2" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Editor avançado (modelos antigos) -->
    <template v-else>
      <div
        class="flex flex-wrap items-center gap-3 border-b border-n-weak bg-n-solid-1 px-4 py-3"
      >
        <button
          class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="cancelEdit"
        >
          <span class="i-lucide-arrow-left" /> {{ tp('BACK') }}
        </button>
        <input
          v-model="editing.name"
          type="text"
          :placeholder="tp('NAME')"
          class="w-48 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
        <input
          v-model="editing.subject"
          type="text"
          :placeholder="tp('SUBJECT')"
          class="min-w-[12rem] flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
        <span class="text-xs text-n-slate-10"
          >{{ tp('VARIABLES_HINT') }} {{ VARIABLES.join(' · ') }}</span
        >
        <button
          class="flex items-center gap-2 rounded-lg bg-n-brand px-4 py-1.5 text-sm font-medium text-white hover:bg-n-brand-dark disabled:opacity-50"
          :disabled="saving"
          @click="saveTemplate"
        >
          <span v-if="saving" class="i-lucide-loader-2 animate-spin" />
          {{ tp('SAVE') }}
        </button>
      </div>
      <div class="min-h-0 flex-1">
        <GrapesEmailEditor
          :key="editing.id || 'new'"
          ref="grapesRef"
          :design="editing.design"
        />
      </div>
    </template>

    <GalleryModal
      ref="galleryRef"
      @create="openBuilder"
      @import="importRef?.open()"
    />
    <ImportHtmlDialog ref="importRef" @created="openBuilder" />
    <ConfirmDialog ref="confirmRef" />
  </div>
</template>
